import { useState, useEffect } from "react";
import axios from "axios";
import ClientLayout from "@/components/ClientLayout";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useRestaurant } from "@/context/RestaurantContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { resolveImageUrl } from "@/lib/resolveImageUrl";
import { toast } from "sonner";
import { Plus, Minus, Package, ShoppingCart, Tag, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const { user } = useAuth();
  const { items, addItem, updateQuantity } = useCart();
  const { selected: restaurant } = useRestaurant();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (restaurant) {
      fetchProducts();
      return;
    }
    setProducts([]);
    setLoading(false);
  }, [search, restaurant]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/products`, {
        params: { search, restaurant_id: restaurant?.restaurant_id || "" },
      });
      setProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  const getCartQuantity = (productId) => {
    const item = items.find((i) => i.product_id === productId);
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (product) => {
    // Use discounted price if available, handle "Consultar" items (price_source NONE)
    const isConsultar = product.price_source === "NONE";
    const priceToUse = isConsultar ? 0 : (product.discounted_price || product.price);
    const productWithInfo = { ...product, price: priceToUse, price_source: product.price_source };
    addItem(productWithInfo, 1);
    toast.success(`${product.name} añadido al carrito`);
  };

  const handleUpdateQuantity = (product, delta) => {
    const currentQty = getCartQuantity(product.product_id);
    const newQty = currentQty + delta;
    
    if (newQty <= 0) {
      updateQuantity(product.product_id, 0);
    } else {
      if (currentQty === 0) {
        const isConsultar = product.price_source === "NONE";
        const priceToUse = isConsultar ? 0 : (product.discounted_price || product.price);
        const productWithInfo = { ...product, price: priceToUse, price_source: product.price_source };
        addItem(productWithInfo, 1);
      } else {
        updateQuantity(product.product_id, newQty);
      }
    }
  };

  const placeholderImages = [
    "/placeholder-1.jpg",
    "/placeholder-2.jpg",
    "/placeholder-3.jpg",
  ];

  const getProductImage = (product, index) => {
    if (product.image_url) return resolveImageUrl(product.image_url);
    return placeholderImages[index % placeholderImages.length];
  };

  const canSeePrices = restaurant?.show_prices_to_client !== false;

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-dashboard">
        {/* Welcome section */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            ¡Hola, {user?.name || user?.company || user?.username || ""}!
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.company && <span className="font-medium">{user.company} · </span>}
            Selecciona los productos y cantidades para tu pedido
          </p>
          {restaurant && (
            <p className="text-sm text-blue-700 mt-3" data-testid="dashboard-current-restaurant">
              Restaurante activo: <span className="font-semibold">{restaurant.name}</span>
            </p>
          )}
          {canSeePrices && <p className="text-xs text-slate-400 mt-2">* Todos los precios sin IVA</p>}
        </div>

        {/* Search (filters assigned products by name or code) */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por nombre o código..."
              className="pl-10"
              data-testid="client-products-search"
            />
          </div>
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-40 w-full rounded-lg mb-4" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              No tienes productos asignados
            </h2>
            <p className="text-slate-500">
              Contacta con el administrador para que te asigne tus productos habituales
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product, index) => {
              const quantity = getCartQuantity(product.product_id);
              const isConsultar = product.price_source === "NONE";
              
              return (
                <Card
                  key={product.product_id}
                  className="product-card bg-white border border-slate-200 shadow-sm"
                  data-testid={`product-card-${product.product_id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-slate-100">
                    <img
                      src={getProductImage(product, index)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    {quantity > 0 && (
                      <div className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" />
                        {quantity}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 sm:p-4">
                    <div className="mb-3">
                      <h3 className="font-semibold text-slate-900 text-lg leading-tight">
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          {!canSeePrices ? (
                            <span className="block text-xs text-slate-400 mt-1" data-testid={`hidden-price-unit-${product.product_id}`}>
                              {product.unit}
                            </span>
                          ) : isConsultar ? (
                            <span className="text-lg font-semibold text-amber-600" data-testid={`consultar-label-${product.product_id}`}>
                              Consultar
                            </span>
                          ) : product.discount > 0 ? (
                            <>
                              <div className="flex items-center gap-1 sm:gap-2">
                                <span className="text-lg text-slate-400 line-through">
                                  {product.unit_price_resolved != null ? product.unit_price_resolved.toFixed(2) : product.price.toFixed(2)}€
                                </span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <Tag className="w-3 h-3" />
                                  -{product.discount}%
                                </span>
                              </div>
                              <span className="text-2xl font-bold text-green-600">
                                {product.discounted_price.toFixed(2)}€
                              </span>
                              <span className="text-sm text-slate-500 ml-1">
                                / {product.unit}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-2xl font-bold text-slate-900">
                                {product.discounted_price != null && product.discounted_price > 0 ? product.discounted_price.toFixed(2) : product.price.toFixed(2)}€
                              </span>
                              <span className="text-sm text-slate-500 ml-1">
                                / {product.unit}
                              </span>
                            </>
                          )}
                          {canSeePrices && !isConsultar && (
                            <span className="block text-xs text-slate-400">sin IVA</span>
                          )}
                        </div>

                        {/* Desktop/Tablet: keep action area on the right */}
                        <div className="hidden sm:block">
                          {quantity === 0 ? (
                            <Button
                              onClick={() => handleAddToCart(product)}
                              className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-4"
                              data-testid={`add-to-cart-${product.product_id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Añadir
                            </Button>
                          ) : (
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10 quantity-btn"
                                onClick={() => handleUpdateQuantity(product, -1)}
                                data-testid={`minus-${product.product_id}`}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="w-8 text-center font-semibold text-lg">
                                {quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-10 w-10 quantity-btn"
                                onClick={() => handleUpdateQuantity(product, 1)}
                                data-testid={`plus-${product.product_id}`}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Mobile: button full width at bottom; keep -/+ on the right as before */}
                      <div className="sm:hidden">
                        {quantity === 0 ? (
                          <Button
                            onClick={() => handleAddToCart(product)}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-4 w-full"
                            data-testid={`add-to-cart-mobile-${product.product_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Añadir
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 quantity-btn"
                              onClick={() => handleUpdateQuantity(product, -1)}
                              data-testid={`minus-${product.product_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-6 text-center font-semibold text-lg">
                              {quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 quantity-btn"
                              onClick={() => handleUpdateQuantity(product, 1)}
                              data-testid={`plus-${product.product_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
